import { useState, useEffect, useContext, useMemo, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ApiContext } from "../../util/Requests";
import {
    filterByPermission,
    groupByNavigationGroup,
    getVisibleGroups,
    navigationItems,
    filterMine
} from "./navigationConfig";
import { SC_NavigationItem } from "../../util/api/SC_NavigationItem";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import "./NavigationMenu.css";
import {faTimes} from "@fortawesome/free-solid-svg-icons/faTimes";
import {faBars} from "@fortawesome/free-solid-svg-icons/faBars";
import FontAwesomeButton from "../cards/FontAwesomeButton";

/**
 * Responsive Navigation Menu Component
 * 
 * Features:
 * - Desktop: Horizontal top navigation bar
 * - Mobile: Hamburger button with dropdown panel
 * - Permission-based visibility (items hidden if user lacks permission)
 * - Configurable via navigationConfig.js
 */
const NavigationMenu = ({ className = "" }) => {
    const { caches } = useContext(ApiContext);
    const location = useLocation();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);

    // Get current permissions (may be undefined on initial load)
    const permissions = caches?.permissions?.value;

    // Filter and group navigation items based on permissions
    const visibleItems = useMemo(() => {
        if (!permissions) return [];
        const filtered = filterMine(navigationItems, permissions);
        return filtered;
    }, [permissions]);

    // Keep all items in a single flat array (no grouping)
    const flatItems = useMemo(() => {
        return visibleItems;
    }, [visibleItems]);

    // Check if current path matches a navigation item
    const isActive = (path) => {
        const currentPath = location.pathname;
        return currentPath === path || currentPath.startsWith(path);
    };

    // Handle click outside to close mobile menu
    useEffect(() => {
        const handleClickOutside = (event) => {
            const menuElement = document.querySelector(".top-navigation");

            // Close menu if click is outside the menu and outside the toggle button
            if (menuElement && !menuElement.contains(event.target)) {
                setIsMobileMenuOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Handle permission change (re-render to update visibility)
    useEffect(() => {
        const handlePermissionChange = () => {
            setIsMobileMenuOpen(false);
        };

        if (caches?.permissions?.ref) {
            caches.permissions.ref.current?.addEventListener?.("change", handlePermissionChange);
        }

        return () => {
            if (caches?.permissions?.ref?.current?.removeEventListener) {
                caches.permissions.ref.current.removeEventListener("change", handlePermissionChange);
            }
        };
    }, [caches]);

    return (
        <nav id={'custom-top-navigation'} className={`top-navigation ${className}`}>
            {/* Desktop Navigation */}
            <div className="nav-desktop">
                <div className="nav-container">
                    {/* Logo/Brand */}
                    <Link to="/dashboard/" className="nav-brand">
                        <FontAwesomeIcon icon="fa-tools" className="nav-brand-icon" />
                        <span className="nav-brand-text">SiteCrew</span>
                    </Link>

                    {/* Navigation Links */}
                    <ul className="nav-links">
                        {flatItems.map((item) => (
                            <NavItem
                                key={item.path}
                                item={item}
                                isActive={isActive(item.path)}
                                showPermissionError={false}
                            />
                        ))}
                    </ul>

                    <FontAwesomeButton
                        extraClasses={"nav-mobile-button"}
                        icon={isMobileMenuOpen ? faTimes : faBars}
                        onClick={() => {
                            console.log("Clikt", isMobileMenuOpen);
                            setIsMobileMenuOpen((v) => {
                                console.log(v, '->', !v);
                                return !v;
                            });
                        }}
                    />
                </div>
            </div>

            {/* Mobile Navigation Panel */}
            {isMobileMenuOpen && (
                <div className="nav-mobile-menu">
                    <div className="nav-mobile-container">
                        <ul className="nav-mobile-links">
                            {flatItems.map((item) => (
                                <NavItem
                                    key={item.path}
                                    item={item}
                                    isActive={isActive(item.path)}
                                    showPermissionError={true}
                                />
                            ))}
                        </ul>
                    </div>
                </div>
            )}
        </nav>
    );
};

/**
 * Individual Navigation Item Component
 * Renders a clickable link with FontAwesome icon
 * Uses client-side navigation to preserve cache state
 */
const NavItem = ({ item, isActive, showPermissionError = false }) => {
    const { caches } = useContext(ApiContext);
    const perms = caches?.permissions?.value;
    const navigate = useNavigate();

    // Check if user has permission for this item
    const hasPermission = item.matchesPermission(perms || {});

    if (!hasPermission) {
        // Permission denied - render nothing (hidden entirely per requirement)
        return null;
    }

    const handleClick = useCallback((event) => {
        if (event.altKey || event.ctrlKey || event.shiftKey)
            return;
        // Prevent default link behavior and use client-side navigation
        // This triggers a soft reload that preserves cache state
        event.preventDefault();
        navigate(item.path);
    }, [item.path, navigate]);

    return (
        <li className={`nav-item ${isActive ? "nav-item-active" : ""}`}>
            <a
                href={item.path}
                className="nav-link"
                onClick={handleClick}
                aria-label={item.title}
                title={item.title}
            >
                <FontAwesomeIcon icon={item.icon} className="nav-link-icon" />
                <span className="nav-link-text">{item.title}</span>
            </a>
        </li>
    );
};

export default NavigationMenu;
